import { ArrowLeft, Loader2, MessageCircle } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { proToPatientMessage, waLink } from "@/lib/whatsapp";

import { DocumentsPanel } from "./DocumentsPanel";
import { DraftEditor } from "./DraftEditor";
import { PaymentVerificationCard } from "./PaymentVerificationCard";
import { ProReferencesPanel } from "./ProReferencesPanel";

/** Carga la solicitud, sincroniza el borrador una sola vez y hace polling mientras la IA genera. */
function useRequestDetail(id) {
  const [req, setReq] = useState(null);
  const [draft, setDraft] = useState({ dx: "", receta: "", nota_evolucion: "" });
  const pollRef = useRef(null);
  const draftLoaded = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/requests/${id}`);
      setReq(data);
      // Solo carga el draft cuando NO se está regenerando (evita pisar el draft nuevo
      // de IA con el viejo durante el polling intermedio).
      if (data.draft && !draftLoaded.current && data.ai_status !== "generating") {
        setDraft({
          dx: data.draft.dx || "",
          receta: data.draft.receta || "",
          nota_evolucion: data.draft.nota_evolucion || "",
        });
        draftLoaded.current = true;
      }
      return data;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    }
  }, [id]);

  useEffect(() => {
    const poll = pollRef;
    return () => clearInterval(poll.current);
  }, []);

  // Polling mientras la IA genera
  useEffect(() => {
    if (req?.ai_status === "generating" && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const data = await load();
        if (data && data.ai_status !== "generating") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (data.ai_status === "ready") toast.success("Borrador de IA generado");
          if (data.ai_status === "error") toast.error("La IA no pudo generar el borrador: " + (data.ai_error || ""));
        }
      }, 3500);
    }
  }, [req?.ai_status, load]);

  return { req, setReq, draft, setDraft, load, draftLoaded };
}

export default function ProRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { req, setReq, draft, setDraft, load, draftLoaded } = useRequestDetail(id);
  const [selectedFile, setSelectedFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    load().then((data) => {
      if (data?.files?.length) setSelectedFile(data.files[0]);
    });
  }, [load]);

  const acceptPayment = async () => {
    try {
      await api.post(`/requests/${id}/payment/accept`);
      toast.success("Pago verificado. La solicitud está en revisión.");
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const rejectPayment = async (reason) => {
    try {
      await api.post(`/requests/${id}/payment/reject`, { reason: reason || "" });
      toast.success("Comprobante rechazado. El paciente fue notificado.");
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const generate = async () => {
    try {
      draftLoaded.current = false;
      await api.post(`/requests/${id}/generate`);
      setReq((r) => ({ ...r, ai_status: "generating" }));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.put(`/requests/${id}/draft`, draft);
      toast.success("Borrador guardado");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const finalize = async () => {
    setFinalizing(true);
    try {
      await api.post(`/requests/${id}/finalize`, draft);
      toast.success("Receta enviada al paciente");
      navigate("/dashboard");
    } catch (e) {
      toast.error(errMsg(e));
      setFinalizing(false);
    }
  };

  if (!req)
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" strokeWidth={1.5} />
        </div>
      </div>
    );

  const personal = req.personal || {};
  const completed = req.status === "completed";
  const paymentAccepted = req.payment_verification === "accepted" || completed;
  const wa = waLink(
    req.patient_whatsapp,
    proToPatientMessage({
      patientName: `${personal.nombres || ""} ${personal.apellidos || ""}`.trim(),
      folio: req.folio || "",
      proName: user?.name || "",
    }),
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="pro-request-detail">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-5" data-testid="pro-back-link">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Volver al panel
        </Link>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">
              {personal.nombres} {personal.apellidos}
            </h1>
            <p className="text-sm text-muted-foreground">
              {personal.edad} años · F. Nac: {personal.fecha_nacimiento}
              {req.folio && ` · Exp: ${req.expediente}/Folio: ${req.folio}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {wa && (
              <a href={wa} target="_blank" rel="noreferrer" data-testid="whatsapp-patient-btn">
                <Button size="sm" className="rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white">
                  <MessageCircle className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> WhatsApp del paciente
                </Button>
              </a>
            )}
            <StatusBadge status={req.status} />
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* IZQUIERDA: pago, documentos e información del paciente */}
          <div className="space-y-5">
            <PaymentVerificationCard
              req={req}
              onAccept={acceptPayment}
              onReject={rejectPayment}
            />
            <div className="border rounded-2xl bg-white p-5">
              <p className="font-heading font-semibold mb-2">Información subjetiva del paciente</p>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap" data-testid="pro-subjective-text">
                {req.subjective || "(sin información)"}
              </p>
              {req.monthly_budget && (
                <p className="text-sm mt-3 pt-3 border-t" data-testid="pro-budget-text">
                  <span className="font-semibold">Presupuesto mensual para tratamiento:</span>{" "}
                  {req.monthly_budget}
                </p>
              )}
            </div>
            <DocumentsPanel req={req} selectedFile={selectedFile} onSelect={setSelectedFile} completed={completed} />
            <ProReferencesPanel req={req} onChange={load} completed={completed} />
          </div>

          {/* DERECHA: editor del borrador IA */}
          <div className="space-y-5">
            <DraftEditor
              req={req}
              draft={draft}
              setDraft={setDraft}
              completed={completed}
              paymentAccepted={paymentAccepted}
              saving={saving}
              finalizing={finalizing}
              onGenerate={generate}
              onSave={saveDraft}
              onFinalize={finalize}
            />
          </div>
        </div>
      </main>
    </div>
  );
}
